import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks";
import { homePath, roleLabel } from "../../utils";
import { WhatsNewModal } from "../WhatsNewModal";
import { BottomNav } from "./BottomNav";
import { MobileNav } from "./MobileNav";
import { SessionSwitchBar, SessionSwitchButton } from "./SessionSwitchMenu";
import { SidebarUserProfile } from "./SidebarUserProfile";

interface AppLayoutProps {
    children: React.ReactNode;
}

const EMPLOYEE_NAV = [
    { name: "Dashboard",       shortName: "Home",      href: "/dashboard",      icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" },
    { name: "My Passport",     shortName: "Passport",  href: "/profile",        icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" },
    { name: "Qualifications",  shortName: "Quals",     href: "/qualifications", icon: "M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" },
    { name: "Work Experience", shortName: "Work",      href: "/experience",     icon: "M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" },
    { name: "Documents",       shortName: "Docs",      href: "/documents",           icon: "M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" },
    { name: "Official Docs",   shortName: "Official",  href: "/official-documents",  icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" },
    { name: "References",      shortName: "Refs",      href: "/references",     icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" },
];

const MANAGER_NAV = [
    { name: "Dashboard",      shortName: "Home",    href: "/dashboard",             icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" },
    { name: "Employees",       shortName: "Team",    href: "/manager/workers",       icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" },
    { name: "My Profile",     shortName: "Profile", href: "/manager/profile",       icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" },
];

const VERIFIER_NAV = [
    { name: "Queue",    shortName: "Queue",    href: "/verifier/queue",    icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" },
    { name: "Workers",  shortName: "Workers",  href: "/verifier/workers",  icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" },
    { name: "Profile",  shortName: "Profile",  href: "/verifier/profile",  icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" },
];

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
    const { logout, accountType } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');

    const navigation =
        accountType === "verifier" ? VERIFIER_NAV : accountType === "manager" ? MANAGER_NAV : EMPLOYEE_NAV;
    const home = homePath(accountType);

    const toggleSidebar = () => {
        setCollapsed(prev => {
            const next = !prev;
            localStorage.setItem('sidebarCollapsed', String(next));
            return next;
        });
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const isActive = (path: string) => {
        if (path === "/dashboard") return location.pathname === path;
        return location.pathname.startsWith(path);
    };

    return (
        <div className="min-h-screen bg-canvas">
            <WhatsNewModal />
            <MobileNav isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} navigation={navigation} />

            <div
                className={`hidden md:fixed md:inset-y-0 md:left-0 md:flex md:flex-col print:hidden bg-shell-900 transition-[width] duration-300 ease-out ${
                    collapsed ? 'md:w-[72px]' : 'md:w-60'
                }`}
            >
                <Link
                    to={home}
                    className={`flex items-center h-16 border-b border-brand-700/50 hover:bg-white/5 transition-colors ${
                        collapsed ? 'justify-center px-2' : 'px-6 gap-2.5'
                    }`}
                    title={collapsed ? 'Passport' : undefined}
                >
                    <img src="/favicon.svg" alt="Passport" className="w-8 h-8 rounded-full flex-shrink-0" />
                    {!collapsed && (
                        <div>
                            <h1 className="text-white font-bold text-lg leading-none">Passport</h1>
                            <span className="text-brand-300 text-xs">{roleLabel(accountType)}</span>
                        </div>
                    )}
                </Link>

                <nav className={`flex-1 py-5 space-y-0.5 overflow-y-auto ${collapsed ? 'px-2' : 'px-3'}`}>
                    {navigation.map(item => {
                        const active = isActive(item.href);
                        return (
                            <Link
                                key={item.name}
                                to={item.href}
                                title={collapsed ? item.name : undefined}
                                className={`flex items-center text-sm font-medium rounded-lg transition-all ${
                                    collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5 gap-3'
                                } ${
                                    active
                                        ? "bg-white/15 text-white"
                                        : "text-brand-200 hover:bg-white/10 hover:text-white"
                                }`}
                            >
                                <svg className={`w-5 h-5 flex-shrink-0 ${active ? 'text-brand-300' : 'text-brand-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                                </svg>
                                {!collapsed && item.name}
                                {!collapsed && active && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-brand-300" />}
                            </Link>
                        );
                    })}
                </nav>

                <div className={`flex-shrink-0 border-t border-brand-700/50 ${collapsed ? 'px-2 py-3' : 'px-3 py-3'}`}>
                    <button
                        type="button"
                        onClick={toggleSidebar}
                        className={`flex items-center w-full text-brand-200/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors ${
                            collapsed ? 'justify-center h-9' : 'gap-2.5 px-2 py-1.5 text-[13px] font-medium'
                        }`}
                        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {collapsed ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        ) : (
                            <>
                                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                <span>Collapse</span>
                            </>
                        )}
                    </button>
                    <div className="my-2 border-t border-white/10" />
                    <SidebarUserProfile collapsed={collapsed} onLogout={handleLogout} />
                </div>
            </div>

            <header className="sticky top-0 z-30 md:hidden print:hidden bg-shell-900 pt-[var(--app-safe-top,env(safe-area-inset-top,0px))]">
                <div className="flex items-center justify-between h-14 px-4">
                    <button
                        onClick={() => setMobileMenuOpen(true)}
                        className="p-2 rounded-lg text-brand-200 hover:text-white hover:bg-white/10 transition-colors"
                        aria-label="Open menu"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </button>
                    <Link to={home} className="flex items-center gap-2 hover:opacity-90 transition-opacity">
                        <img src="/favicon.svg" alt="Passport" className="w-6 h-6 rounded-full" />
                        <h1 className="text-white font-bold text-lg">Passport</h1>
                    </Link>
                    <SessionSwitchButton />
                </div>
            </header>

            <div className={`print:pl-0 transition-[padding] duration-300 ease-out ${collapsed ? 'md:pl-[72px]' : 'md:pl-60'}`}>
                <SessionSwitchBar />
                <main className="py-4 md:py-6 pb-[calc(6rem+var(--app-safe-bottom,env(safe-area-inset-bottom,0px)))] md:pb-6 print:p-0">
                    <div className={`${accountType === "employee" ? "max-w-5xl" : "max-w-none"} mx-auto px-4 sm:px-6 lg:px-8 print:max-w-none print:p-0`}>{children}</div>
                </main>
            </div>

            <BottomNav navigation={navigation} />
        </div>
    );
};
